import camelcaseKeys from 'camelcase-keys';
import ky, { isHTTPError } from 'ky';
import snakecaseKeys from 'snakecase-keys';

import { getEnv } from '@/utils/env';

import { UUID_REGEX } from '../constants/uuid-regex';

interface ApiErrorResponseBody {
  error: Record<string, unknown>;
  message: string;
}

function isApiErrorResponseBody(data: unknown): data is ApiErrorResponseBody {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const { error, message } = data as ApiErrorResponseBody;
  return Boolean(error && message);
}

export class ServerError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`Internal server error (${status})`);
    this.name = 'ServerError';
    this.status = status;
  }
}

export const baseFetch = ky.create({
  retry: 0,
  prefix: `${getEnv('API_URL')}`,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  parseJson: (text) => {
    let parsedText = text;

    if (Object.keys(parsedText).length) {
      parsedText = camelcaseKeys(JSON.parse(parsedText), {
        deep: true,
        exclude: [UUID_REGEX],
        stopPaths: ['data.training_content', 'data.description_blocks', 'uid', 'data.answers.values'],
      });
    }

    return parsedText;
  },
  stringifyJson: (data) => {
    let parsedData = data;

    if (data && Object.keys(data).length > 0) {
      parsedData = snakecaseKeys(data as Record<string, unknown>, { exclude: ['data.answers.values'] });
    }

    return JSON.stringify(parsedData);
  },
  hooks: {
    afterResponse: [
      ({ response }) => {
        if (response.status >= 500) {
          throw new ServerError(response.status);
        }
      },
    ],
    beforeError: [
      ({ error }) => {
        if (isHTTPError(error) && isApiErrorResponseBody(error.data)) {
          error.name = error.name || 'Error';
          error.message = error.data.message;
        }

        return error;
      },
    ],
  },
});
